/**
 * ConfirmDialog — 되돌리기 어려운 동작 앞에 서는 확인 창.
 *
 * **속을 Astryx 로 갈아 끼웠다 (2026-08-18).** 그전까지는 Flutter 화면을 옮길 때
 * 만든 손수 마크업이었다(`confirm_dialog.dart` 변환). 그래서 Astryx 규칙 밖에
 * 있었고, 실제로 어긋났다:
 *
 *   · `background-color: white` 가 박혀 있어 **다크 모드에서도 흰 카드**였다
 *   · 버튼이 --color-gray-30 / --color-ink-black 같은 §3 @deprecated 별칭을 썼다
 *     (템플릿 시절 잔재. 앱의 다른 버튼과 색·높이·글자가 전부 달랐다)
 *   · 글자 크기가 16/12px 로 박혀 서체 사다리 밖이었다
 *
 * 지금은 Astryx 가 그린다:
 *   · 취소가 있는 보통 경우  → AlertDialog
 *   · 취소가 없는 강제 안내  → Dialog(purpose="required")
 *     AlertDialog 는 취소를 끌 수 없다. 필수 업데이트처럼 "닫을 수 없어야"
 *     하는 자리가 있어서(UpdatePrompt) 그쪽만 Dialog 로 간다.
 *
 * **바깥에서 보는 props 는 그대로다** — 부르는 곳 아홉 군데를 건드리지 않았다.
 *
 * ⚠ portal 을 직접 쓰지 않는다. 예전에는 routeMotion 의 will-change: transform 이
 *   fixed 의 담는 상자를 만들어서 그걸 portal 로 피해 갔는데, Astryx 는 <dialog>
 *   를 쓰고 이건 top layer 라 애초에 그 문제가 없다.
 */
import { useTranslation } from 'react-i18next';
import { AlertDialog } from '@astryxdesign/core/AlertDialog';
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog';
import { Layout, LayoutContent, LayoutFooter, HStack } from '@astryxdesign/core/Layout';
import { Button } from '@astryxdesign/core/Button';
import { Text } from '@astryxdesign/core/Text';

export type ConfirmDialogVariant = 'default' | 'destructive';

/**
 * 폭 — 좁은 화면에서 넘치지 않게 뷰포트에서 좌우 여백(16px×2)을 뺀 값과 겨룬다.
 * Astryx 기본값 400px 을 그대로 두면 375px 기기에서 화면 밖으로 나간다.
 */
const DIALOG_WIDTH = 'min(400px, calc(100vw - 32px))';

interface ConfirmDialogProps {
  /**
   * Modal mode controls.
   * - `open === undefined` → 모달 껍데기 없이 내용만 렌더 (Showcase 등 시각 표시용).
   * - `open === true` → 모달로 뜬다.
   * - `open === false` → null.
   */
  open?: boolean;
  /** Backdrop 클릭 / ESC 시 호출. modal 모드에서만 동작. */
  onClose?: () => void;
  variant?: ConfirmDialogVariant;
  title?: string;
  description?: string;
  secondaryText?: string;
  primaryText?: string;
  onSecondaryPressed?: () => void;
  onPrimaryPressed?: () => void;
  /** true 면 secondary 버튼 미노출 (Figma 1599:74719 — 확인-only 안내 다이얼로그). */
  hideSecondary?: boolean;
}

function ConfirmDialog({
  open,
  onClose,
  variant = 'default',
  title = 'Title',
  description = 'Description',
  secondaryText,
  primaryText,
  onSecondaryPressed,
  onPrimaryPressed,
  hideSecondary = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation();
  const secondaryLabel = secondaryText ?? t('action.cancel');
  const primaryLabel = primaryText ?? t('action.confirm');
  const isModalMode = open !== undefined;
  const isInline = !isModalMode;

  if (isModalMode && !open) return null;

  /**
   * 닫힘은 한 갈래로 모은다. Astryx 는 '취소를 눌렀다' 와 'ESC·backdrop 으로
   * 닫았다' 를 구분해 주지 않는다(둘 다 onOpenChange(false)).
   *
   * 부르는 곳 아홉 군데를 확인했더니 onSecondaryPressed 와 onClose 가 **언제나
   * 같은 함수**였다. 탈퇴 화면만 처리 중일 때 둘 다 undefined 로 두는데, 그때는
   * 아무것도 부르지 않으므로 부모의 open 이 true 로 남아 창이 그대로 열려 있다
   * — 그게 원하는 동작이다(처리 중엔 못 닫는다).
   */
  const handleOpenChange = (next: boolean) => {
    if (next) return;
    (onSecondaryPressed ?? onClose)?.();
  };

  // 취소가 없는 강제 안내 — AlertDialog 는 취소를 끌 수 없어 Dialog 로 간다.
  // purpose="required" 가 ESC·backdrop 을 막는다(예전 'onClose 미전달' 과 같은 뜻).
  if (hideSecondary) {
    return (
      <Dialog
        isOpen
        isInline={isInline}
        onOpenChange={handleOpenChange}
        purpose="required"
        width={DIALOG_WIDTH}
      >
        <Layout
          header={<DialogHeader title={title} />}
          content={
            <LayoutContent>
              {/* description 에 \n 이 들어오는 자리가 있다 (탈퇴·안내) */}
              <Text type="body" style={{ whiteSpace: 'pre-line' }}>
                {description}
              </Text>
            </LayoutContent>
          }
          footer={
            <LayoutFooter>
              <HStack gap={2} hAlign="end">
                <Button
                  label={primaryLabel}
                  variant={variant === 'destructive' ? 'destructive' : 'primary'}
                  onClick={onPrimaryPressed}
                />
              </HStack>
            </LayoutFooter>
          }
        />
      </Dialog>
    );
  }

  return (
    <AlertDialog
      isOpen
      isInline={isInline}
      onOpenChange={handleOpenChange}
      title={title}
      description={description}
      cancelLabel={secondaryLabel}
      actionLabel={primaryLabel}
      actionVariant={variant === 'destructive' ? 'destructive' : 'primary'}
      onAction={() => onPrimaryPressed?.()}
      width={DIALOG_WIDTH}
    />
  );
}

export default ConfirmDialog;
